import React, { useState, useEffect, useRef, useMemo, useId } from 'react';
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonIcon,
  IonButton,
  IonButtons,
  IonList,
  IonItem,
  IonLabel,
  useIonViewDidEnter,
  useIonViewWillLeave,
  useIonRouter,
} from '@ionic/react';
import { add, search, close, closeCircle } from 'ionicons/icons';
import { FooterDock, FooterDockRow } from '@/modules/footer';
import { TopicLogic } from './logic';
import SnowballMenu from '@/modules/user/SnowballMenu';
import { ACTIONS, RESOURCES } from '@/contexts/CasbinContext';
import PageRefresher from '@/components/PageRefresher';
import { hasOtherTabs, cleanupTabId } from '@/services/crossTabCommunication';
import { ActionGuard } from '@/components/ActionGuard';
import { ActionGuardContainer } from '@/components/ActionGuardContainer';
import { useTopicState } from './TopicStateContext';
import { selectAllTopics } from './reducer';
import { useUserContext } from '@/modules/user/useUserContext';
import {
  pxToRem,
  footerControlSurfaceStyle,
  footerControlRoundButtonStyle,
} from '@/modules/config/constants';
import { useAppConfig } from '@/modules/config';
import { enabledTopics } from './topicTypeRegistry';
import { betaCreateTopicConsolation } from '@/conditional_feature/beta_testing';
import { topicGardenEnabled } from '@/conditional_feature/topic_garden_enabled';
import { TopicTypePickerSheet } from '@/modules/topic_garden';
import type { TopicTypeId } from './types';

// Set by `/topics/new` (CreateTopicGate) just before it redirects here, and taken
// by the list as it enters, so the redirect arrives with the type picker open.
let typePickerRequested = false;

/** Ask the topics list to open the type picker sheet when it next comes into view. */
export function requestTypePickerOnEnter(): void {
  typePickerRequested = true;
}

function takeTypePickerRequest(): boolean {
  const requested = typePickerRequested;
  typePickerRequested = false;
  return requested;
}

const Topics: React.FC = () => {
  const pageRef = useRef<HTMLElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useIonRouter();
  const searchInputId = useId();
  const { state, refetchTopics } = useTopicState();
  const { features } = useAppConfig();
  const topics = useMemo(
    () => enabledTopics(selectAllTopics(state), features),
    [state, features]
  );
  const { userId, claims } = useUserContext();

  const [query, setQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  useEffect(() => {
    void hasOtherTabs();
  }, []);

  // Refetch when auth/verification changes (e.g. the user re-verifies a phone
  // number and becomes an invited user), so by-invitation-only topics that
  // just became visible appear — and ones no longer visible drop off the list.
  // Skip the initial mount: TopicStateProvider already fetches once.
  const prevAuthRef = useRef<{ userId?: string; claims: string } | null>(null);
  useEffect(() => {
    const current = { userId, claims: JSON.stringify(claims) };
    if (prevAuthRef.current === null) {
      prevAuthRef.current = current;
      return;
    }
    if (
      prevAuthRef.current.userId !== current.userId ||
      prevAuthRef.current.claims !== current.claims
    ) {
      refetchTopics();
    }
    prevAuthRef.current = current;
  }, [userId, claims, refetchTopics]);

  useEffect(() => {
    return () => {
      void cleanupTabId();
    };
  }, []);

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  // With the garden flag on, + opens the type picker sheet over this list
  // (and `/topics/new` lands here with it open, see CreateTopicGate); picking a
  // type pushes its create form, so Back from the form returns here.
  const gardenEnabled = topicGardenEnabled(features);
  const [typePickerOpen, setTypePickerOpen] = useState(false);
  useIonViewDidEnter(() => {
    if (takeTypePickerRequest()) setTypePickerOpen(true);
  });

  const handleStartTopicClick = () => {
    if (gardenEnabled) {
      setTypePickerOpen(true);
    } else {
      router.push('/topics/new', 'forward', 'push');
    }
  };

  const handleTypePickerDismiss = (picked: TopicTypeId | null) => {
    setTypePickerOpen(false);
    if (picked) router.push(`/topics/new-${picked}`, 'forward', 'push');
  };

  const normalizedQuery = query.trim();
  const isSearching = isSearchFocused || normalizedQuery.length > 0;

  const filteredTopics = useMemo(
    () =>
      normalizedQuery
        ? topics.filter(a =>
            a.title.toLowerCase().includes(normalizedQuery.toLowerCase())
          )
        : topics,
    [topics, normalizedQuery]
  );

  const clearQueryAndFocus = () => {
    setQuery('');
    inputRef.current?.focus();
  };

  const closeSearch = () => {
    setQuery('');
    setIsSearchFocused(false);
    inputRef.current?.blur();
  };

  return (
    <IonPage ref={pageRef}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Topics</IonTitle>
          <IonButtons slot="end">
            <SnowballMenu />
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      {/* White scroll surface so the list reads as one continuous white sheet —
          white behind the rows and all the way past the last one (and under the
          scrim-less footer dock), instead of the page grey showing below. */}
      <IonContent style={{ '--background': '#fff' }}>
        <PageRefresher />
        {/* Extend the bottom padding by the keyboard inset (iOS, published as
            --footer-keyboard-inset by the keyboard-pinned search row) so the last
            topics can scroll clear of the keyboard and the lifted search row. */}
        <div
          style={{
            paddingBottom: 'calc(100px + var(--footer-keyboard-inset, 0px))',
          }}
        >
          {filteredTopics.length > 0 && (
            <IonList style={{ background: '#fff' }}>
              {filteredTopics.map((topic, index) => (
                <div key={topic.id} style={{ backgroundColor: '#fff' }}>
                  <IonItem
                    button
                    detail={false}
                    lines="none"
                    routerLink={`/topics/${topic.id}`}
                    data-topic-id={topic.id}
                    style={
                      {
                        '--padding-start': '20px',
                        '--inner-padding-end': '20px',
                        '--min-height': '62px',
                        '--background': '#fff',
                        '--border-color': 'transparent',
                        '--border-style': 'none',
                        '--border-width': '0px',
                        '--inner-border-width': '0px',
                      } as React.CSSProperties
                    }
                  >
                    <div
                      slot="start"
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 8,
                        flex: '0 0 44px',
                        marginRight: 12,
                        overflow: 'hidden',
                        background: 'var(--ion-color-light)',
                        position: 'relative',
                      }}
                      aria-hidden="true"
                    >
                      {topic.thumbnailImageData && (
                        <img
                          src={topic.thumbnailImageData}
                          alt=""
                          style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      )}
                    </div>
                    <IonLabel>
                      <h2
                        style={{
                          margin: 0,
                          color: '#111',
                          fontSize: pxToRem(17),
                          lineHeight: pxToRem(22),
                          fontWeight: 400,
                        }}
                      >
                        {topic.title}
                      </h2>
                      <p
                        style={{
                          margin: '1px 0 0',
                          color: '#8e8e93',
                          fontSize: pxToRem(14),
                          lineHeight: pxToRem(18),
                        }}
                      >
                        {TopicLogic.formatTopicDate(
                          TopicLogic.startTimeAsDateObj(topic),
                          topic.timezone
                        )}
                      </p>
                    </IonLabel>
                  </IonItem>
                  {index < filteredTopics.length - 1 && (
                    <div
                      aria-hidden="true"
                      style={{
                        height: 1,
                        backgroundColor: '#e5e5ea',
                        marginLeft: 76,
                        pointerEvents: 'none',
                      }}
                    />
                  )}
                </div>
              ))}
            </IonList>
          )}
          {normalizedQuery && filteredTopics.length === 0 && (
            <div
              style={{
                textAlign: 'center',
                color: '#8e8e93',
                padding: '40px 20px',
              }}
            >
              No results for &ldquo;{normalizedQuery}&rdquo;
            </div>
          )}
        </div>
      </IonContent>
      {/* No scrim: every control in the dock (the search pill and the
          close/create buttons) paints its own solid background, so the
          scrolling topics list can show through the gaps between them without
          any text jumbling with a button's label. */}
      <FooterDock background="transparent">
        {/* Not `bare`: the content box supplies the safe-area bottom clearance
            so the pill row clears the home indicator while sitting at the screen
            bottom. */}
        <FooterDockRow pin="keyboard">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '0 20px',
            }}
          >
            <div
              style={{
                flex: 1,
                height: 44,
                borderRadius: 22,
                ...footerControlSurfaceStyle,
                boxShadow: '0 14px 36px rgba(0,0,0,0.12)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '0 10px 0 14px',
                minWidth: 0,
              }}
            >
              <label
                htmlFor={searchInputId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flex: 1,
                  minWidth: 0,
                  cursor: 'text',
                }}
              >
                <IonIcon
                  icon={search}
                  aria-hidden="true"
                  style={{
                    color: '#8e8e93',
                    fontSize: pxToRem(22),
                    flex: '0 0 auto',
                  }}
                />
                <input
                  id={searchInputId}
                  ref={inputRef}
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  placeholder="Search topics"
                  inputMode="search"
                  enterKeyHint="search"
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="off"
                  aria-label="Search topics"
                  style={{
                    border: 0,
                    outline: 0,
                    background: 'transparent',
                    minWidth: 0,
                    flex: 1,
                    height: 36,
                    color: '#000',
                    fontSize: pxToRem(17),
                    lineHeight: pxToRem(22),
                    padding: 0,
                  }}
                />
              </label>
              {query.length > 0 && (
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    clearQueryAndFocus();
                  }}
                  aria-label="Clear search"
                  style={{
                    border: 0,
                    background: 'transparent',
                    color: '#8e8e93',
                    padding: 0,
                    width: 30,
                    height: 30,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <IonIcon
                    icon={closeCircle}
                    style={{ fontSize: pxToRem(22) }}
                  />
                </button>
              )}
            </div>

            {isSearching ? (
              <IonButton
                fill="clear"
                onClick={closeSearch}
                aria-label="Close search"
                className="footer-glass-button footer-glass-round"
                style={
                  {
                    ...footerControlRoundButtonStyle,
                    width: 44,
                    height: 44,
                    '--border-radius': '50%',
                    flex: '0 0 44px',
                  } as React.CSSProperties
                }
              >
                <IonIcon
                  slot="icon-only"
                  icon={close}
                  style={{ fontSize: pxToRem(28) }}
                />
              </IonButton>
            ) : (
              <ActionGuardContainer>
                <ActionGuard
                  disableMode="on-demand"
                  action={ACTIONS.CREATE}
                  resource={RESOURCES.TOPICS}
                  helpText={{
                    singleVerification:
                      'Verify your email to create topics [Verify]',
                    updateProfile:
                      'Update your profile to create topics [Update]',
                    setupProfile:
                      'Set up your profile to create topics [Set up]',
                  }}
                  // When the betaTesting gate is on, a non-beta user has no
                  // verification path to create topics (isUnlockable is false).
                  // Rather than hide the button, keep it enabled and toast an
                  // invitation to the contact page. Off → undefined → unchanged.
                  onDemandConsolation={betaCreateTopicConsolation(features)}
                  guardedElem={
                    <IonButton
                      fill="clear"
                      onClick={handleStartTopicClick}
                      aria-label="New topic"
                      data-testid="create-topic-button"
                      className="footer-glass-button footer-glass-round"
                      style={
                        {
                          ...footerControlRoundButtonStyle,
                          width: 44,
                          height: 44,
                          '--border-radius': '50%',
                          flex: '0 0 44px',
                        } as React.CSSProperties
                      }
                    >
                      <IonIcon
                        slot="icon-only"
                        icon={add}
                        style={{ fontSize: pxToRem(28) }}
                      />
                    </IonButton>
                  }
                />
              </ActionGuardContainer>
            )}
          </div>
        </FooterDockRow>
      </FooterDock>
      {gardenEnabled && (
        <TopicTypePickerSheet
          isOpen={typePickerOpen}
          onDidDismiss={handleTypePickerDismiss}
        />
      )}
    </IonPage>
  );
};

export default Topics;
